import { useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Shared "add family members as extra seats" picker for membership
 * checkout. Call `selectSeats()` before starting checkout — it fetches
 * eligible sub-users (adults not already holding a seat), and either
 * resolves immediately with [] (nothing eligible, no dialog shown) or
 * opens the dialog and resolves once the member picks Continue/Just me.
 * Render `<SubUserSeatDialog {...seatDialogProps} />` once, anywhere in
 * the calling component's JSX.
 */
export function useSubUserSeatSelect() {
    const [open, setOpen] = useState(false);
    const [eligible, setEligible] = useState([]);
    const [dependantsByProvider, setDependantsByProvider] = useState(new Map());
    const [selected, setSelected] = useState([]);
    const resolverRef = useRef(null);

    const settle = (ids) => {
        setOpen(false);
        const resolve = resolverRef.current;
        resolverRef.current = null;
        if (resolve) resolve(ids);
    };

    const selectSeats = async () => {
        let eligibleSubs = [];
        let grouped = new Map();
        try {
            const { data } = await api.get("/member/sub-users");
            const all = data || [];
            eligibleSubs = all.filter(
                (s) => !s.is_dependant && s.membershipInForce !== true
            );
            // Dependants are informational only here (never billed, never
            // selectable) — only shown nested under a provider that's
            // itself selectable in this dialog.
            const eligibleIds = new Set(eligibleSubs.map((s) => s.id));
            all
                .filter(
                    (s) =>
                        s.is_dependant &&
                        s.primary_provider_id &&
                        eligibleIds.has(s.primary_provider_id)
                )
                .forEach((d) => {
                    const list = grouped.get(d.primary_provider_id) || [];
                    list.push(d);
                    grouped.set(d.primary_provider_id, list);
                });
        } catch (_e) {
            // fail open — don't block checkout on a failed fetch
            eligibleSubs = [];
            grouped = new Map();
        }
        if (eligibleSubs.length === 0) return [];
        setEligible(eligibleSubs);
        setDependantsByProvider(grouped);
        setSelected([]);
        setOpen(true);
        return new Promise((resolve) => {
            resolverRef.current = resolve;
        });
    };

    const toggle = (id) =>
        setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    return {
        selectSeats,
        seatDialogProps: {
            open,
            eligible,
            dependantsByProvider,
            selected,
            onToggle: toggle,
            onSkip: () => settle([]),
            onContinue: () => settle(selected),
            onOpenChange: (o) => { if (!o) settle([]); },
        },
    };
}

// Stable, module-scope component — must NOT be defined inside the hook
// above. A function recreated on every render reads to React as a brand
// new component type, so it would remount (losing animation state,
// visibly flickering) every time `selected` changes — e.g. every
// checkbox click. See react/no-unstable-nested-components.
export function SubUserSeatDialog({
    open,
    eligible,
    dependantsByProvider,
    selected,
    onToggle,
    onSkip,
    onContinue,
    onOpenChange,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="subuser-seat-dialog" className="max-w-md bg-white">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl text-navy-700">
                        Add family members to this plan?
                    </DialogTitle>
                    <DialogDescription>
                        Selected members will be added as extra seats on the same
                        subscription. You'll be billed for all seats on one card.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2.5 max-h-56 overflow-y-auto">
                    {eligible.map((s) => (
                        <div key={s.id}>
                            <label
                                htmlFor={`seat-${s.id}`}
                                className="flex items-center gap-2 text-sm text-navy-700 cursor-pointer"
                            >
                                <input
                                    id={`seat-${s.id}`}
                                    type="checkbox"
                                    data-testid={`subuser-seat-check-${s.id}`}
                                    checked={selected.includes(s.id)}
                                    onChange={() => onToggle(s.id)}
                                    className="accent-navy-700 h-4 w-4"
                                />
                                <span>
                                    {s.name}{" "}
                                    <span className="text-xs text-navy-400">
                                        · {s.relationship}
                                    </span>
                                </span>
                            </label>
                            {(dependantsByProvider.get(s.id) || []).map((d) => (
                                <div
                                    key={d.id}
                                    data-testid={`subuser-seat-dependant-${d.id}`}
                                    className="ml-6 flex items-center gap-1.5 text-xs text-navy-500 mt-1"
                                >
                                    <span className="text-emerald-600">↳</span>
                                    <span>
                                        {d.name}{" "}
                                        <span className="text-navy-400">
                                            · Dependant · free
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onSkip}
                        data-testid="subuser-seat-skip"
                        className="rounded-full"
                    >
                        Just me
                    </Button>
                    <Button
                        onClick={onContinue}
                        data-testid="subuser-seat-continue"
                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                    >
                        Continue{selected.length > 0 ? ` (${1 + selected.length} seats)` : ""}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
